import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, MapPin, Users, Shield, Star, Clock, Heart, Compass, Plane, Mountain } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=2400&q=90&auto=format&fit=crop";

const FEATURED = [
  { title: "Kashmir", img: "https://images.pexels.com/photos/2670898/pexels-photo-2670898.jpeg", tag: "Heaven on Earth", days: "7D · 6N" },
  { title: "Char Dham", img: "https://images.pexels.com/photos/16152405/pexels-photo-16152405.jpeg", tag: "Sacred Yatra", days: "12D · 11N" },
  { title: "Bali", img: "https://images.pexels.com/photos/1029604/pexels-photo-1029604.jpeg", tag: "Tropical Paradise", days: "6D · 5N" },
  { title: "Dubai", img: "https://images.pexels.com/photos/618079/pexels-photo-618079.jpeg", tag: "Luxury Calling", days: "5D · 4N" },
  { title: "Kerala", img: "https://images.pexels.com/photos/962464/pexels-photo-962464.jpeg", tag: "Backwaters", days: "6D · 5N" },
  { title: "Switzerland", img: "https://images.pexels.com/photos/417173/pexels-photo-417173.jpeg", tag: "Alpine Magic", days: "10D · 9N" },
];

const SERVICES = [
  { icon: MapPin, title: "Domestic Journeys", desc: "Discover the soul of India — Himalayas to the backwaters.", link: "/domestic", img: "https://images.pexels.com/photos/19019326/pexels-photo-19019326.jpeg" },
  { icon: Plane, title: "International Escapes", desc: "Carefully crafted overseas adventures, visa to villa.", link: "/international", img: "https://images.pexels.com/photos/1287460/pexels-photo-1287460.jpeg" },
  { icon: Users, title: "Group Departures", desc: "Travel together with curated fixed-date group tours.", link: "/group", img: "https://images.pexels.com/photos/3935702/pexels-photo-3935702.jpeg" },
];

const STATS = [
  { value: "10+", label: "Years" },
  { value: "5K+", label: "Happy Travelers" },
  { value: "50+", label: "Destinations" },
  { value: "4.9★", label: "Rated" },
];

const REASONS = [
  { icon: Shield, title: "Trusted & Verified", desc: "A decade of crafting trips, hundreds of 5-star reviews." },
  { icon: Heart, title: "Personalized Care", desc: "Every itinerary tailored to your pace, interests, and budget." },
  { icon: Clock, title: "24/7 Support", desc: "On-call assistance throughout your journey." },
  { icon: Star, title: "Premium Stays", desc: "Hand-picked hotels, curated experiences, comfortable transfers." },
];

const TESTIMONIALS = [
  { name: "Priya & Rohan Mehta", trip: "Maldives Honeymoon", quote: "From start to finish, every detail was handled with such care. The overwater villa was a dream.", img: "https://images.pexels.com/photos/1287460/pexels-photo-1287460.jpeg" },
  { name: "The Sharma Family", trip: "Char Dham Yatra", quote: "A truly spiritual experience. The hotels, food, transport — everything was top-notch.", img: "https://images.pexels.com/photos/16152405/pexels-photo-16152405.jpeg" },
  { name: "Aman & Friends · Group of 12", trip: "Dubai Group Tour", quote: "Group travel can be chaotic, but Lucky ji's planning made it effortless. Unforgettable.", img: "https://images.pexels.com/photos/618079/pexels-photo-618079.jpeg" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.listPackages({ featured: true }).then((d) => setFeatured(d.items || [])).catch(() => {});
  }, []);

  return (
    <div className="relative">
      {/* ============================================
          HERO - cinematic, glass, luxurious
          ============================================ */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        {/* BG image with cinematic overlay */}
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1A3A]/65 via-[#0A1A3A]/25 to-[#0EA5E9]/20" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#EAF3FF]" />
        </div>
        <div className="orb orb-blue animate-float" style={{ width: 360, height: 360, top: 100, right: 80 }} />
        <div className="orb orb-indigo" style={{ width: 280, height: 280, bottom: 80, left: 60 }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 w-full">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 glass-dark px-4 py-2 rounded-full text-[11px] font-semibold tracking-[0.2em] text-white mb-7">
              <Sparkles className="w-3 h-3" /> AI-CRAFTED ITINERARIES · DESIGNED FOR YOU
            </div>
            <h1 className="font-display font-bold tracking-tight text-[44px] sm:text-[64px] lg:text-[88px] leading-[0.95] text-white drop-shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
              The art of <span className="font-serif-italic font-medium" style={{ background: "linear-gradient(135deg, #93C5FD 0%, #38BDF8 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>unhurried</span><br />
              <span style={{ background: "linear-gradient(135deg, #DBEAFE 0%, #38BDF8 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>journeys.</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-white max-w-2xl font-serif-italic leading-relaxed drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
              Curated by <span className="font-semibold not-italic text-white">Lucky Jeswani</span> — from Char Dham
              pilgrimages to Maldivian honeymoons, every detail considered, every moment composed.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/contact">
                <Button size="lg" className="btn-luxe rounded-2xl text-white h-13 px-7 text-base" data-testid="hero-cta-plan-trip">
                  Plan My Journey <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/domestic">
                <Button size="lg" variant="outline" className="glass border-white/60 rounded-2xl h-13 px-7 text-base text-[#0A1A3A] hover:bg-white/70" data-testid="hero-cta-browse-tours">
                  Browse Tours
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Stats card floating */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-16 lg:mt-20 max-w-3xl glass-strong p-7 lg:p-9 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-4 relative"
          >
            {STATS.map((s, i) => (
              <div key={s.label} className={`text-center ${i > 0 ? "border-l border-white/50" : ""}`}>
                <div className="font-display font-bold text-3xl lg:text-4xl text-gradient-blue">{s.value}</div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-600 mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ============================================
          FEATURED DESTINATIONS
          ============================================ */}
      <section className="relative py-24 lg:py-32 aurora-section">
        <div className="orb orb-cyan" style={{ width: 400, height: 400, top: 100, left: -100 }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-2xl mb-14">
            <div className="text-[10px] font-semibold tracking-[0.3em] text-[#1D4ED8] mb-3">— FEATURED ESCAPES</div>
            <h2 className="font-display font-bold text-4xl lg:text-6xl text-[#0A1A3A] leading-[1.05]">
              Destinations <span className="font-serif-italic font-medium text-gradient-blue">loved</span> by our travelers.
            </h2>
            <p className="mt-4 text-slate-600 text-lg font-serif-italic">Each itinerary refined, each moment crafted with intention.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {(featured.length > 0 ? featured.slice(0, 6) : FEATURED).map((f, i) => {
              const isPkg = !!f.image_url;
              const img = isPkg ? f.image_url : f.img;
              const title = f.title;
              const tag = isPkg ? f.destination : f.tag;
              const meta = isPkg ? f.duration : f.days;
              const link = isPkg ? `/package/${f.id}` : "/domestic";
              const variants = ["img-notched-tl", "img-notched-tr", "img-notched-br", "img-notched-bl"];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                >
                  <Link to={link} data-testid={`featured-destination-card-${i}`} className="block group">
                    <div className="glass glass-hover p-3 transition-all duration-300 cursor-pointer">
                      <div className={`relative h-64 overflow-hidden ${variants[i % variants.length]}`}>
                        <img src={img} alt={title} className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1A3A]/70 via-transparent to-transparent" />
                        <div className="absolute bottom-4 left-5 right-5 text-white">
                          <div className="text-[10px] tracking-[0.25em] uppercase opacity-90">{tag}</div>
                          <div className="font-display font-bold text-2xl mt-1">{title}</div>
                        </div>
                        <div className="absolute top-4 right-4 glass-dark px-3 py-1 rounded-full text-[10px] font-medium tracking-wider text-white">
                          {meta}
                        </div>
                      </div>
                      <div className="px-3 py-4 flex items-center justify-between">
                        <span className="text-sm text-slate-600 font-serif-italic">Explore the experience</span>
                        <ArrowRight className="w-4 h-4 text-[#1D4ED8] group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================
          SERVICES (3 cards with images)
          ============================================ */}
      <section className="relative py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-[10px] font-semibold tracking-[0.3em] text-[#1D4ED8] mb-3">— WHAT WE OFFER</div>
            <h2 className="font-display font-bold text-4xl lg:text-5xl text-[#0A1A3A]">
              Three ways to <span className="font-serif-italic font-medium text-gradient-blue">wander</span>.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
              >
                <Link to={s.link} className="block group">
                  <div className="glass glass-hover h-full p-2 transition-all duration-300">
                    <div className="relative h-48 rounded-2xl overflow-hidden">
                      <img src={s.img} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-br from-[#0A1A3A]/60 to-transparent" />
                      <div className="absolute top-4 left-4 w-12 h-12 rounded-2xl glass-dark flex items-center justify-center">
                        <s.icon className="w-5 h-5 text-white" />
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-display font-bold text-2xl text-[#0A1A3A]">{s.title}</h3>
                      <p className="mt-2 text-slate-600 font-serif-italic">{s.desc}</p>
                      <div className="mt-4 text-sm text-[#1D4ED8] font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                        Discover <ArrowRight className="w-3.5 h-3.5" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          WHY CHOOSE US — split layout with image collage
          ============================================ */}
      <section className="relative py-24 lg:py-32 aurora-section">
        <div className="orb orb-indigo" style={{ width: 380, height: 380, top: 60, right: -100 }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center relative">
          <div>
            <div className="text-[10px] font-semibold tracking-[0.3em] text-[#1D4ED8] mb-3">— WHY HAPPY JOURNEY 4 U</div>
            <h2 className="font-display font-bold text-4xl lg:text-5xl text-[#0A1A3A] leading-tight">
              Trips that feel <span className="font-serif-italic font-medium text-gradient-blue">personal</span>, never templated.
            </h2>
            <p className="mt-4 text-slate-600 text-lg font-serif-italic">Curated by someone who treats your journey like their own.</p>
            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              {REASONS.map((r, i) => (
                <motion.div
                  key={r.title}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="flex gap-3"
                >
                  <div className="w-11 h-11 rounded-2xl glass flex items-center justify-center shrink-0">
                    <r.icon className="w-5 h-5 text-[#1D4ED8]" />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-lg text-[#0A1A3A]">{r.title}</div>
                    <div className="text-sm text-slate-600 mt-0.5">{r.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          {/* Collage of notched images */}
          <div className="relative h-[520px]">
            <img src="https://images.pexels.com/photos/29375777/pexels-photo-29375777.jpeg" className="absolute top-0 left-0 w-[60%] h-[55%] img-notched-tl object-cover shadow-2xl shadow-blue-900/15" alt="" />
            <img src="https://images.pexels.com/photos/19019326/pexels-photo-19019326.jpeg" className="absolute top-[5%] right-0 w-[45%] h-[40%] img-notched-tr object-cover shadow-2xl shadow-blue-900/15" alt="" />
            <img src="https://images.pexels.com/photos/32111236/pexels-photo-32111236.jpeg" className="absolute bottom-0 left-[10%] w-[50%] h-[50%] img-notched-bl object-cover shadow-2xl shadow-blue-900/15" alt="" />
            <img src="https://images.pexels.com/photos/3155666/pexels-photo-3155666.jpeg" className="absolute bottom-[5%] right-[5%] w-[42%] h-[42%] img-notched-br object-cover shadow-2xl shadow-blue-900/15" alt="" />
            {/* Floating glass badge */}
            <div className="absolute top-[42%] left-[45%] glass-strong rounded-2xl px-4 py-3 text-center shadow-xl">
              <div className="font-display font-bold text-2xl text-gradient-blue">10+</div>
              <div className="text-[9px] uppercase tracking-widest text-slate-600">Years of trust</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          TESTIMONIALS — luxury cards with photo
          ============================================ */}
      <section className="relative py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-[10px] font-semibold tracking-[0.3em] text-[#1D4ED8] mb-3">— TRAVELER STORIES</div>
            <h2 className="font-display font-bold text-4xl lg:text-5xl text-[#0A1A3A]">
              Real journeys, <span className="font-serif-italic font-medium text-gradient-blue">unforgettable</span> memories.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="glass glass-hover p-7 transition-all"
              >
                <div className="flex items-center gap-1 text-[#F59E0B]">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-current" />)}
                </div>
                <p className="mt-4 text-slate-700 leading-relaxed font-serif-italic text-lg">"{t.quote}"</p>
                <div className="mt-6 pt-6 border-t border-white/60 flex items-center gap-3">
                  <img src={t.img} className="w-12 h-12 rounded-full object-cover ring-2 ring-white" alt="" />
                  <div>
                    <div className="font-display font-semibold text-[#0A1A3A]">{t.name}</div>
                    <div className="text-xs text-slate-500 tracking-wide">{t.trip}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          FINAL CTA — cinematic band
          ============================================ */}
      <section className="relative py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-[2rem] overflow-hidden">
            <img src="https://images.pexels.com/photos/1287460/pexels-photo-1287460.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#0A1A3A]/85 via-[#1E3A8A]/70 to-[#0EA5E9]/50" />
            <div className="relative z-10 p-10 lg:p-20 text-white flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div>
                <div className="text-[10px] font-semibold tracking-[0.3em] text-cyan-200 mb-3">— READY?</div>
                <h2 className="font-display font-bold text-4xl lg:text-6xl leading-tight">
                  Your next chapter<br />
                  <span className="font-serif-italic font-medium">awaits.</span>
                </h2>
                <p className="mt-4 text-cyan-50 text-lg font-serif-italic max-w-md">Share your dream destination. We'll craft the rest with care.</p>
              </div>
              <Link to="/contact">
                <Button size="lg" className="rounded-2xl bg-white text-[#1D4ED8] hover:bg-blue-50 h-14 px-8 text-base shadow-2xl" data-testid="cta-band-inquire-button">
                  Begin Planning <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
